import { useState, memo } from "react";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { ThemedTrashIcon } from "assets/CXFIcons/Triggers";
import SoftButton from "components/SoftButton";
import { COMMON_COMPONENT_TYPES } from "constants/flowDesigner";
import Tooltip from "@mui/material/Tooltip";
import { useSelector } from "react-redux";
import { useReactFlow } from "reactflow";

const ButtonDeleteNode = memo(({ openDialog }) => {
   const { t } = useTranslation("flowDesigner");
   return (
      <Tooltip title={t("buttons.delete")} placement="bottom-start">
         <IconButton aria-label="delete" size="small" onClick={openDialog}>
            <ThemedTrashIcon width="20px" height="20px" color="currentColor" />
         </IconButton>
      </Tooltip>
   );
});
const DeleteNodeMenuItem = ({ deleteNode, node, allowEditWorkflow, setShowTooltip }) => {
   const { t } = useTranslation("flowDesigner");
   const { currentSavedFlow } = useSelector((state) => state.designer.flows);
   const { getNodes } = useReactFlow();
   const isActiveFlow = currentSavedFlow?.shared_status === "active";
   const name = node?.customLabel || node?.label;
   const isConditional = node.type === COMMON_COMPONENT_TYPES.SELECTION || node.type === COMMON_COMPONENT_TYPES.CONDITIONAL;
   const contentMessage = isConditional ? t("dialogs.delete_step.conditional_content", { name }) : t("dialogs.delete_step.content", { name });
   const [showDialog, setShowDialog] = useState(false);
   const [linkedVariableReferences, setLinkedVariableReferences] = useState([]);
   const linkedVariableReferencesByName = linkedVariableReferences.reduce((referencesByName, reference) => {
      const references = referencesByName.get(reference.referenceName) || [];
      references.push(reference);
      referencesByName.set(reference.referenceName, references);
      return referencesByName;
   }, new Map());
   const isVariablesDeletionBlocked = node?.type === COMMON_COMPONENT_TYPES.VARIABLES && linkedVariableReferences.length > 0;

   const openDialog = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setShowTooltip(false);
      const referencePrefix = `variables.${node?.componentKey}.`;
      const references = node?.type === COMMON_COMPONENT_TYPES.VARIABLES
         ? getNodes()
            .filter((flowNode) => flowNode.data?.variable_reference?.startsWith(referencePrefix))
            .map((flowNode) => ({
               componentKey: flowNode.data?.componentKey || flowNode.id,
               name: flowNode.data?.customLabel || flowNode.data?.label || flowNode.data?.flowElement?.action || flowNode.type || "Unnamed node",
               referenceName: flowNode.data.variable_reference.slice(referencePrefix.length),
            }))
         : [];
      setLinkedVariableReferences(references);
      setShowDialog(true);
   };

   const closeDialog = (e) => {
      e?.stopPropagation?.();
      setShowDialog(false);
   };

   const deleteStep = (e) => {
      e?.stopPropagation?.();
      closeDialog();
      deleteNode();
   };

   return (
      <>
         <ButtonDeleteNode openDialog={openDialog} disabled={isActiveFlow || !allowEditWorkflow} />
         <Dialog onClick={(e) => e?.stopPropagation?.()} open={showDialog} onClose={closeDialog} aria-labelledby="deleteGroupDialogTitle" aria-describedby="deleteGroupDialogDescription">
            <DialogTitle id="deleteGroupDialogTitle">{isVariablesDeletionBlocked ? `Cannot delete “${name}”` : t("dialogs.delete_step.title")}</DialogTitle>
            <DialogContent>
               {isVariablesDeletionBlocked ? (
                  <>
                     <DialogContentText id="deleteGroupDialogDescription">
                        This Variables component has Output References used by:
                     </DialogContentText>
                     {[...linkedVariableReferencesByName.entries()].map(([referenceName, references]) => (
                        <div key={referenceName}>
                           <DialogTitle component="div" sx={{ mt: 1, p: 0, fontSize: "inherit" }}>{referenceName}</DialogTitle>
                           <ul style={{ margin: "4px 0 0", paddingLeft: 24 }}>
                              {references.map(({ componentKey, name }) => (
                                 <li key={componentKey}>
                                    {name} ({componentKey})
                                 </li>
                              ))}
                           </ul>
                        </div>
                     ))}
                     <DialogContentText sx={{ mt: 2 }}>
                        Remove or change these node Variable References before deleting this Variables component.
                     </DialogContentText>
                  </>
               ) : (
                  <DialogContentText id="deleteGroupDialogDescription">{contentMessage}</DialogContentText>
               )}
            </DialogContent>
            <DialogActions>
               <SoftButton variant="outlined" color="primary" onClick={closeDialog}>
                  {isVariablesDeletionBlocked ? "Close" : t("buttons.cancel")}
               </SoftButton>
               {!isVariablesDeletionBlocked && (
                  <SoftButton onClick={(e) => deleteStep(e)} color="primary">
                     {t("buttons.delete")}
                  </SoftButton>
               )}
            </DialogActions>
         </Dialog>
      </>
   );
};

export default memo(DeleteNodeMenuItem);
